import { queryOptions, QueryClient, FetchQueryOptions } from "@tanstack/react-query";
import { withFirebaseRetry } from "../utils/retryUtils";
import { getProfile } from "../firebase/profileService";
import { getAllExperiences } from "../firebase/experienceService";
import { seedExperiences } from "../firebase/seedExperiences";
import { getAllEducation } from "../firebase/educationService";
import { getAllPublications } from "../firebase/publicationService";
import { getAllPubPackages } from "../firebase/pubPackageService";
import { getAllStarterTemplates } from "../firebase/starterTemplateService";
import {
  getAllBlogs,
  getFeaturedBlogs,
  getAllBlogCategories,
} from "../firebase/blogService";
import { getAllCourses } from "../firebase/courseService";
import { getAllCourseChapters } from "../firebase/courseChapterService";
import { getAllCourseModules } from "../firebase/courseModuleService";
import { getAllArticles } from "../firebase/articleService";
import {
  getAllProjects,
  getFeaturedProjects,
  getProjectById,
} from "../firebase/projectService";
import { getActiveCategories } from "../firebase/categoryService";
import { profileKeys } from "../hooks/useProfileQuery";
import { projectKeys } from "../hooks/useProjectQuery";
import { publicationKeys } from "../hooks/usePublicationQuery";
import {
  pubPackageKeys,
  starterTemplateKeys,
  blogKeys,
} from "../hooks/useOpenSourceQuery";

/**
 * Single source of truth for every public content query.
 *
 * Contexts, pages and the nav-link prefetcher all build their queries from
 * these options, so a page visited once (or a link hovered) is served from
 * the TanStack cache instead of hitting Firestore again.
 */

export const experienceKeys = { all: ["experiences"] as const };
export const educationKeys = { all: ["education"] as const };
export const courseKeys = {
  all: ["course"] as const,
  courses: () => [...courseKeys.all, "courses"] as const,
  chapters: () => [...courseKeys.all, "chapters"] as const,
  modules: () => [...courseKeys.all, "modules"] as const,
  articles: () => [...courseKeys.all, "articles"] as const,
};
export const categoryKeys = { active: ["categories", "active"] as const };

export const profileQuery = queryOptions({
  queryKey: profileKeys.detail(),
  queryFn: getProfile,
});

export const experiencesQuery = queryOptions({
  queryKey: experienceKeys.all,
  queryFn: () =>
    withFirebaseRetry(async () => {
      // Seeds only when the collection is empty
      await seedExperiences();
      return getAllExperiences();
    }),
});

export const educationQuery = queryOptions({
  queryKey: educationKeys.all,
  queryFn: () => withFirebaseRetry(getAllEducation),
});

export const publicationsQuery = queryOptions({
  queryKey: publicationKeys.lists(),
  queryFn: () => withFirebaseRetry(getAllPublications),
});

// Featured packages/templates are a client-side filter of the full list,
// so they're derived via `select` instead of a second Firestore read.
export const selectFeatured = <T extends { featured?: boolean }>(items: T[]) =>
  items.filter((item) => item.featured);

export const pubPackagesQuery = queryOptions({
  queryKey: pubPackageKeys.lists(),
  queryFn: () => withFirebaseRetry(getAllPubPackages),
});

export const starterTemplatesQuery = queryOptions({
  queryKey: starterTemplateKeys.lists(),
  queryFn: () => withFirebaseRetry(getAllStarterTemplates),
});

export const blogsQuery = queryOptions({
  queryKey: blogKeys.lists(),
  queryFn: () =>
    withFirebaseRetry(async () => (await getAllBlogs()).blogs),
});

export const featuredBlogsQuery = queryOptions({
  queryKey: blogKeys.featured(),
  queryFn: () => withFirebaseRetry(getFeaturedBlogs),
});

export const blogCategoriesQuery = queryOptions({
  queryKey: blogKeys.categories(),
  queryFn: getAllBlogCategories,
});

export const coursesQuery = queryOptions({
  queryKey: courseKeys.courses(),
  queryFn: () => withFirebaseRetry(getAllCourses),
});

export const courseChaptersQuery = queryOptions({
  queryKey: courseKeys.chapters(),
  queryFn: () => withFirebaseRetry(getAllCourseChapters),
});

export const courseModulesQuery = queryOptions({
  queryKey: courseKeys.modules(),
  queryFn: () => withFirebaseRetry(getAllCourseModules),
});

export const articlesQuery = queryOptions({
  queryKey: courseKeys.articles(),
  queryFn: () => withFirebaseRetry(getAllArticles),
});

export const projectsQuery = queryOptions({
  queryKey: projectKeys.lists(),
  queryFn: getAllProjects,
});

export const featuredProjectsQuery = queryOptions({
  queryKey: projectKeys.featured(),
  queryFn: getFeaturedProjects,
});

export const projectQuery = (id: string) =>
  queryOptions({
    queryKey: projectKeys.detail(id),
    queryFn: () => getProjectById(id),
    enabled: !!id,
  });

export const activeCategoriesQuery = queryOptions({
  queryKey: categoryKeys.active,
  queryFn: getActiveCategories,
});

/** Queries each top-level page needs on first render, keyed by route path. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const routeQueries: Record<string, FetchQueryOptions<any, any, any, any>[]> = {
  "/": [profileQuery, featuredProjectsQuery, publicationsQuery, pubPackagesQuery, starterTemplatesQuery, blogsQuery, featuredBlogsQuery],
  "/projects": [projectsQuery, activeCategoriesQuery],
  "/open-source": [pubPackagesQuery, starterTemplatesQuery, blogsQuery, featuredBlogsQuery],
  "/resume": [profileQuery, experiencesQuery, educationQuery, publicationsQuery],
  "/publications": [profileQuery, publicationsQuery],
  "/pub-packages": [pubPackagesQuery],
  "/starter-templates": [starterTemplatesQuery],
  "/writing": [coursesQuery, courseChaptersQuery, courseModulesQuery, articlesQuery],
  "/contact": [profileQuery],
};

/**
 * Warm the cache for a route when the visitor shows intent (hover/focus on a nav link),
 * so the page usually renders with data instead of a skeleton. Already-fresh data is skipped.
 */
export const prefetchRoute = (queryClient: QueryClient, path: string) => {
  routeQueries[path]?.forEach((options) => queryClient.prefetchQuery(options));
};
